// =============================================================================
// MÓDULO ANIMATION MANAGER
// =============================================================================

export class AnimationManager {
    constructor(config = {}) {
        this.config = {
            enableParallax: true,
            enableHoverEffects: true,
            enableScrollAnimations: true,
            parallaxSpeed: 0.5,
            ...config
        };

        this.init();
    }

    init() {
        if (this.config.enableParallax) {
            this.initParallaxEffects();
        }

        if (this.config.enableHoverEffects) {
            this.initHoverEffects();
        }

        if (this.config.enableScrollAnimations) {
            this.initScrollAnimations();
        }

        this.initLottieAnimations();
        this.initCustomAnimations();
    }

    initParallaxEffects() {
        const parallaxElements = document.querySelectorAll('[data-parallax]');
        
        if (parallaxElements.length === 0) return;

        const handleScroll = () => {
            const scrollTop = window.pageYOffset;

            parallaxElements.forEach(element => {
                const speed = parseFloat(element.dataset.parallax) || this.config.parallaxSpeed;
                const yPos = -(scrollTop * speed);
                element.style.transform = `translateY(${yPos}px)`;
            });
        };

        // Throttle scroll events
        let ticking = false;
        window.addEventListener('scroll', () => {
            if (!ticking) {
                requestAnimationFrame(() => {
                    handleScroll();
                    ticking = false;
                });
                ticking = true;
            }
        });
    }

    initHoverEffects() {
        // Efecto hover para tarjetas
        const cards = document.querySelectorAll('.soft-card, .info-pill, .icon-wrap');
        
        cards.forEach(card => {
            card.addEventListener('mouseenter', () => {
                card.style.transform = 'translateY(-5px) scale(1.02)';
                card.style.transition = 'all 0.3s ease';
            });

            card.addEventListener('mouseleave', () => {
                card.style.transform = 'translateY(0) scale(1)';
            });
        });

        // Efecto hover para imágenes de galería
        const galleryImages = document.querySelectorAll('.owl-carousel img');
        
        galleryImages.forEach(img => {
            img.addEventListener('mouseenter', () => {
                img.style.transform = 'rotate(-2deg) scale(1.05)';
                img.style.transition = 'all 0.3s ease';
            });

            img.addEventListener('mouseleave', () => {
                img.style.transform = 'rotate(0deg) scale(1)';
            });
        });
    }

    initScrollAnimations() {
        // Animaciones basadas en scroll
        const observerOptions = {
            threshold: 0.1,
            rootMargin: '0px 0px -50px 0px'
        };

        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    const element = entry.target;
                    const animationType = element.dataset.scrollAnimation;
                    
                    this.applyScrollAnimation(element, animationType);
                    observer.unobserve(element);
                }
            });
        }, observerOptions);

        // Observar elementos con animaciones de scroll
        document.querySelectorAll('[data-scroll-animation]').forEach(el => {
            observer.observe(el);
        });
    }

    applyScrollAnimation(element, animationType) {
        element.classList.add('animate-in');

        switch (animationType) {
            case 'fadeInUp':
                element.style.animation = 'fadeInUp 0.8s ease forwards';
                break;
            case 'fadeInLeft':
                element.style.animation = 'fadeInLeft 0.8s ease forwards';
                break;
            case 'fadeInRight':
                element.style.animation = 'fadeInRight 0.8s ease forwards';
                break;
            case 'zoomIn':
                element.style.animation = 'zoomIn 0.6s ease forwards';
                break;
            case 'bounceIn':
                element.style.animation = 'bounceIn 1s ease forwards';
                break;
            default:
                element.style.animation = 'fadeIn 0.8s ease forwards';
        }
    }

    initLottieAnimations() {
        // Configurar animaciones Lottie para que se reproduzcan cuando entren en vista
        const lottieElements = document.querySelectorAll('lottie-player');
        
        const lottieObserver = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                const lottie = entry.target;
                
                if (entry.isIntersecting) {
                    lottie.play();
                } else {
                    // Pausar si está fuera de vista para mejorar performance
                    if (lottie.dataset.pauseOutOfView !== 'false') {
                        lottie.pause();
                    }
                }
            });
        }, { threshold: 0.2 });

        lottieElements.forEach(lottie => {
            lottieObserver.observe(lottie);
            
            // Configurar loop y autoplay basado en data attributes
            if (lottie.dataset.loop === 'false') {
                lottie.loop = false;
            }
            
            if (lottie.dataset.autoplay === 'false') {
                lottie.autoplay = false;
            }
        });
    }

    initCustomAnimations() {
        // Animación de shimmer para metal bars
        const metalBars = document.querySelectorAll('.metal-bar');
        metalBars.forEach(bar => {
            // La animación CSS ya está definida, solo agregamos interactividad
            bar.addEventListener('mouseenter', () => {
                bar.style.animationDuration = '1.5s';
            });

            bar.addEventListener('mouseleave', () => {
                bar.style.animationDuration = '3.5s';
            });
        });

        // Animación de typing para textos especiales
        const typingElements = document.querySelectorAll('[data-typing]');
        typingElements.forEach(element => {
            this.initTypingAnimation(element);
        });

        // Animación de contador para números
        const counterElements = document.querySelectorAll('[data-counter]');
        counterElements.forEach(element => {
            this.initCounterAnimation(element);
        });
    }

    initTypingAnimation(element) {
        const text = element.textContent;
        const speed = parseInt(element.dataset.typingSpeed) || 100;
        
        element.textContent = '';
        element.style.borderRight = '2px solid';
        element.style.animation = 'blink 1s infinite';

        let i = 0;
        const typeWriter = () => {
            if (i < text.length) {
                element.textContent += text.charAt(i);
                i++;
                setTimeout(typeWriter, speed);
            } else {
                element.style.borderRight = 'none';
                element.style.animation = 'none';
            }
        };

        // Iniciar cuando el elemento entre en vista
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    typeWriter();
                    observer.unobserve(entry.target);
                }
            });
        });

        observer.observe(element);
    }

    initCounterAnimation(element) {
        const target = parseInt(element.dataset.counter);
        const duration = parseInt(element.dataset.counterDuration) || 2000;
        const increment = target / (duration / 16); // 60fps

        let current = 0;
        element.textContent = '0';

        const updateCounter = () => {
            current += increment;
            if (current < target) {
                element.textContent = Math.floor(current);
                requestAnimationFrame(updateCounter);
            } else {
                element.textContent = target;
            }
        };

        // Iniciar cuando el elemento entre en vista
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    updateCounter();
                    observer.unobserve(entry.target);
                }
            });
        });

        observer.observe(element);
    }

    // Método para agregar animaciones CSS dinámicamente
    addCustomCSS() {
        const style = document.createElement('style');
        style.textContent = `
            @keyframes fadeIn {
                from { opacity: 0; }
                to { opacity: 1; }
            }

            @keyframes fadeInUp {
                from { 
                    opacity: 0; 
                    transform: translateY(30px); 
                }
                to { 
                    opacity: 1; 
                    transform: translateY(0); 
                }
            }

            @keyframes fadeInLeft {
                from { 
                    opacity: 0; 
                    transform: translateX(-30px); 
                }
                to { 
                    opacity: 1; 
                    transform: translateX(0); 
                }
            }

            @keyframes fadeInRight {
                from { 
                    opacity: 0; 
                    transform: translateX(30px); 
                }
                to { 
                    opacity: 1; 
                    transform: translateX(0); 
                }
            }

            @keyframes zoomIn {
                from { 
                    opacity: 0; 
                    transform: scale(0.8); 
                }
                to { 
                    opacity: 1; 
                    transform: scale(1); 
                }
            }

            @keyframes bounceIn {
                0% { 
                    opacity: 0; 
                    transform: scale(0.3); 
                }
                50% { 
                    opacity: 1; 
                    transform: scale(1.05); 
                }
                70% { 
                    transform: scale(0.9); 
                }
                100% { 
                    opacity: 1; 
                    transform: scale(1); 
                }
            }

            @keyframes blink {
                0%, 50% { border-color: transparent; }
                51%, 100% { border-color: currentColor; }
            }

            .animate-in {
                opacity: 0;
            }
        `;
        
        document.head.appendChild(style);
    }
}

// Agregar CSS personalizado al cargar
document.addEventListener('DOMContentLoaded', () => {
    const animationManager = new AnimationManager();
    animationManager.addCustomCSS();
});